// src/services/paymentService.js
// Handles Razorpay payment flow: create order → open checkout → verify payment

const BACKEND_URL = process.env.REACT_APP_API_URL || '';

/**
 * Step 1: Call our backend to create a Razorpay order.
 * Backend hits Razorpay API and returns orderId + amount.
 *
 * @param {number} amount - Total amount in INR (integer, paise)
 * @param {string|null} idToken - Firebase ID token for auth (null for guests)
 * @returns {Object} - { razorpayOrderId, amount, currency }
 */
export const createRazorpayOrder = async (amount, idToken) => {
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

  const response = await fetch(`${BACKEND_URL}/api/payment/create-order`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ amount: amount * 100 }), // Convert to paise
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.message || 'Failed to create payment order');
  }

  return response.json(); // { razorpayOrderId, amount, currency }
};

/**
 * Step 2: Open Razorpay payment modal.
 * Returns a Promise that resolves with payment details on success,
 * or rejects with an error on failure/dismiss.
 *
 * @param {Object} options - Razorpay options
 * @returns {Promise<Object>} - { razorpay_payment_id, razorpay_order_id, razorpay_signature }
 */
export const openRazorpayCheckout = (options) => {
  return new Promise((resolve, reject) => {
    if (!window.Razorpay) {
      reject(new Error('Razorpay SDK not loaded. Add the script tag to public/index.html.'));
      return;
    }

    const rzp = new window.Razorpay({
      ...options,
      handler: (response) => resolve(response),
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled by user')),
      },
    });

    rzp.on('payment.failed', (response) => {
      reject(new Error(response.error?.description || 'Payment failed'));
    });

    rzp.open();
  });
};

/**
 * Step 3: Verify payment signature with our backend.
 * Backend uses Razorpay secret to validate the signature, then creates the order.
 * Sends full order data (items, pricing, shippingAddress, shippingMethod) so the
 * backend can create the order atomically — no separate POST /api/orders call needed.
 * Returns full order object — shipment_id will be null (async BullMQ queue).
 * Handles 409 Conflict (duplicate payment) gracefully.
 *
 * @param {Object} paymentData      - { razorpay_payment_id, razorpay_order_id, razorpay_signature }
 * @param {string|null} idToken     - Firebase ID token (null for guests)
 * @param {number} attempt          - Internal: retry attempt count (do not pass manually)
 * @param {Object} guestInfo        - { guestEmail, guestName } — only for guest checkouts
 * @param {Object} orderData        - { items, pricing, shippingAddress, shippingMethod } — required
 * @returns {Object}                - { success, orderId, order } — order.shiprocket.shipment_id may be null
 */
export const verifyRazorpayPayment = async (
  paymentData,
  idToken,
  attempt = 1,
  guestInfo = {},
  orderData = {},
) => {
  const MAX_ATTEMPTS = 3;
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

    const body = {
      razorpayOrderId: paymentData.razorpay_order_id,
      razorpayPaymentId: paymentData.razorpay_payment_id,
      razorpaySignature: paymentData.razorpay_signature,
      // Order creation data — backend creates order atomically during verify
      items: orderData.items || [],
      pricing: orderData.pricing || {},
      shippingAddress: orderData.shippingAddress || {},
      shippingMethod: orderData.shippingMethod || 'standard',
    };
    if (!idToken && guestInfo.guestEmail) {
      body.guestEmail = guestInfo.guestEmail;
      body.guestName = guestInfo.guestName || '';
    }

    const response = await fetch(`${BACKEND_URL}/api/payment/verify`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    // 409 = payment already processed — fetch the existing order instead
    if (response.status === 409) {
      const data = await response.json().catch(() => ({}));
      return { success: true, orderId: data.orderId, order: data.order || null, duplicate: true };
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error ?? err.message ?? 'Payment verification failed');
    }

    // Returns { success, orderId, order } — order.shiprocket.shipment_id will be null initially
    return response.json();
  } catch (err) {
    // Retry on network failure (not on 4xx errors) with exponential backoff
    const isNetworkError = !err.message.includes('verification failed') &&
      !err.message.includes('Invalid') && !err.message.includes('400');
    if (isNetworkError && attempt < MAX_ATTEMPTS) {
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
      return verifyRazorpayPayment(paymentData, idToken, attempt + 1, guestInfo, orderData);
    }
    throw err;
  }
};

/**
 * Full payment flow: create order → open modal → verify.
 * This is the single function Checkout.jsx calls.
 * Returns the verified order from backend — shipment_id will be null (async queue).
 *
 * @param {Object} params
 * @param {number} params.amount          - Total in INR
 * @param {string|null} params.idToken    - Firebase ID token (null for guests)
 * @param {Object|null} params.userInfo   - { displayName, email } — null for guests
 * @param {string|null} params.guestName  - Guest full name (null for authenticated users)
 * @param {string|null} params.guestEmail - Guest email (null for authenticated users)
 * @param {string} params.prefillName     - Name to pre-fill in Razorpay modal
 * @param {string} params.prefillEmail    - Email to pre-fill in Razorpay modal
 * @param {string} params.razorpayKeyId   - process.env.REACT_APP_RAZORPAY_KEY
 * @param {Array}  params.items           - Cart items mapped to backend shape
 * @param {Object} params.pricing         - { subtotal, shipping, tax, total }
 * @param {Object} params.shippingAddress - { fullName, phone, address, city, state, pincode }
 * @param {string} params.shippingMethod  - 'standard' | 'express'
 * @returns {Object} - { success, orderId, order } — order has shiprocket.status = 'pending'
 */
export const initiatePayment = async ({
  amount,
  idToken,
  userInfo,
  guestName = null,
  guestEmail = null,
  prefillName,
  prefillEmail,
  razorpayKeyId,
  items = [],
  pricing = {},
  shippingAddress = {},
  shippingMethod = 'standard',
}) => {
  // 1. Create Razorpay order via backend (optional auth)
  const { razorpayOrderId, currency } = await createRazorpayOrder(amount, idToken);

  // 2. Open Razorpay modal — prefill from either userInfo or guest fields
  const resolvedName = prefillName || userInfo?.displayName || guestName || '';
  const resolvedEmail = prefillEmail || userInfo?.email || guestEmail || '';

  const paymentResult = await openRazorpayCheckout({
    key: razorpayKeyId,
    amount: amount * 100,
    currency: currency || 'INR',
    name: 'Panch Tatva',
    description: 'Elemental Wellness Products',
    order_id: razorpayOrderId,
    prefill: {
      name: resolvedName,
      email: resolvedEmail,
    },
    theme: { color: '#C6A664' },
  });

  // 3. Verify with backend — pass guest info + full order data so backend creates order atomically
  const verificationResult = await verifyRazorpayPayment(
    paymentResult,
    idToken,
    1,
    { guestEmail, guestName },
    { items, pricing, shippingAddress, shippingMethod },
  );

  return verificationResult; // { success, orderId, order }
};
